import type { Container } from "pixi.js";
import { clamp, type Rect, type Vec2 } from "../world/geometry";
import type { EntityId } from "../world/Entity";
import type { World } from "../world/World";

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

const FOLLOW_SPEED_PER_SECOND = 4;

/** Owns the world container's pan/zoom. World coordinates stay independent of screen coordinates. */
export class Camera {
  private position: Vec2 = { x: 0, y: 0 };
  private zoomLevel = 1;
  private bounds?: Rect;
  private followTarget?: EntityId;

  constructor(
    private readonly worldContainer: Container,
    private viewport: Viewport,
  ) {}

  panTo(position: Vec2): void {
    this.position = position;
    this.apply();
  }

  zoomTo(zoom: number): void {
    this.zoomLevel = zoom;
    this.apply();
  }

  setBounds(bounds: Rect): void {
    this.bounds = bounds;
    this.apply();
  }

  focus(entityId: EntityId): void {
    this.followTarget = entityId;
  }

  resize(viewport: Viewport): void {
    this.viewport = viewport;
    this.apply();
  }

  update(deltaSeconds: number, world: World): void {
    if (this.followTarget) {
      const entity = world.getEntity(this.followTarget);
      if (entity) {
        const t = Math.min(1, deltaSeconds * FOLLOW_SPEED_PER_SECOND);
        this.position = {
          x: this.position.x + (entity.transform.position.x - this.position.x) * t,
          y: this.position.y + (entity.transform.position.y - this.position.y) * t,
        };
      }
    }
    this.apply();
  }

  screenToWorld(point: Vec2): Vec2 {
    return {
      x: (point.x - this.worldContainer.x) / this.zoomLevel,
      y: (point.y - this.worldContainer.y) / this.zoomLevel,
    };
  }

  worldToScreen(point: Vec2): Vec2 {
    return {
      x: point.x * this.zoomLevel + this.worldContainer.x,
      y: point.y * this.zoomLevel + this.worldContainer.y,
    };
  }

  private apply(): void {
    let { x, y } = this.position;
    if (this.bounds) {
      const halfW = this.viewport.width / (2 * this.zoomLevel);
      const halfH = this.viewport.height / (2 * this.zoomLevel);
      x = clamp(x, this.bounds.x + halfW, this.bounds.x + this.bounds.width - halfW);
      y = clamp(y, this.bounds.y + halfH, this.bounds.y + this.bounds.height - halfH);
    }
    this.worldContainer.scale.set(this.zoomLevel);
    this.worldContainer.position.set(
      this.viewport.width / 2 - x * this.zoomLevel,
      this.viewport.height / 2 - y * this.zoomLevel,
    );
  }
}
